import type { ReactNode } from 'react';
import { color, font, topicColors } from '../styles/theme';
import { num } from '../lib/format';
import { useAnalytics, useOverview } from '../api/queries';
import { useAuth } from '../auth/AuthContext';
import { Card, CardTitle, PageTitle, ColHead, Loading, ErrorNote, Meter } from '../components/ui';

const DAY_PLOT = 160;
const R = 58;
const C = 2 * Math.PI * R;

/**
 * Donut of submissions by form — the design's "Inquiries by type" chart.
 *
 * Each arc is one dash of the circumference with the running total as its
 * offset, so the slices butt up against each other without any path maths.
 */
function Donut({
  slices,
  total,
}: {
  slices: Array<{ label: string; value: number; color: string }>;
  total: number;
}) {
  let acc = 0;
  return (
    <svg viewBox="0 0 160 160" style={{ width: 150, height: 150, display: 'block' }}>
      <circle cx="80" cy="80" r={R} fill="none" stroke={color.line} strokeWidth={26} />
      {slices.map((s) => {
        const frac = total > 0 ? s.value / total : 0;
        const len = frac * C;
        const el = (
          <circle
            key={s.label}
            cx="80"
            cy="80"
            r={R}
            fill="none"
            stroke={s.color}
            strokeWidth={26}
            // A 3px gap keeps adjacent slices readable at this stroke width.
            strokeDasharray={`${Math.max(0, len - 3).toFixed(1)} ${(C - len + 3).toFixed(1)}`}
            strokeDashoffset={(-acc * C).toFixed(1)}
            transform="rotate(-90 80 80)"
          />
        );
        acc += frac;
        return el;
      })}
    </svg>
  );
}

export function Analytics({
  /** Rendered inside the dashboard's reporting tab, which owns the heading. */
  embedded = false,
  tabs,
}: { embedded?: boolean; tabs?: ReactNode } = {}) {
  const q = useAnalytics();
  // The submissions-by-form card, for a role that may read submissions; the
  // rest of the tab is traffic, which analytics:read alone opens.
  const overview = useOverview(useAuth().can('submissions:read'));

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote message={(q.error as Error).message} />;
  if (!q.data) return null;

  const a = q.data;
  const toneColor = (t: string) => (t === 'up' ? color.green : t === 'down' ? color.red : color.muted);

  if (a.provider === 'none') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }} className="fade-in">
        {!embedded && (
          <PageTitle sub="Traffic to the public ENACT Lab site.">Traffic analytics</PageTitle>
        )}
        {tabs}
        <Card style={{ padding: 44, textAlign: 'center' }}>
          <CardTitle size={18} style={{ marginBottom: 8 }}>
            No analytics provider connected
          </CardTitle>
          <p
            style={{
              fontSize: 13.5,
              color: color.muted,
              margin: '0 auto',
              maxWidth: 460,
              lineHeight: 1.65,
            }}
          >
            This section stays empty until the site is reporting real traffic. Set
            <code style={{ margin: '0 4px', color: color.ink }}>ANALYTICS_PROVIDER=plausible</code>
            on the API, with a site ID and API key, and the figures appear here.
          </p>
          <p style={{ fontSize: 12, color: color.muted, margin: '18px 0 0' }}>
            Nothing patient-identifying reaches the analytics provider — it sees page paths and
            referrers only.
          </p>
        </Card>
      </div>
    );
  }

  const dayMax = Math.max(...a.visits, 1);
  const o = overview.data;
  const donutSlices = (o?.formStats ?? []).map((f, i) => ({
    label: f.short,
    value: f.total,
    color: topicColors[i % topicColors.length]!,
  }));
  const donutTotal = o?.total ?? 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }} className="fade-in">
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <h1
          style={{
            fontFamily: font.display,
            fontWeight: 600,
            fontSize: 27,
            color: color.navy,
            margin: 0,
            flex: 1,
          }}
        >
          Traffic analytics
        </h1>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 12.5,
            color: color.ink,
            background: color.white,
            border: `1px solid ${color.line}`,
            borderRadius: 999,
            padding: '7px 14px',
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: a.configured ? '#2f9e6e' : '#d97b6c',
              flex: 'none',
            }}
          />
          {a.provider}
        </span>
      </div>
      {tabs}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(215px,1fr))',
          gap: 16,
        }}
      >
        {a.cards.map((c) => (
          <Card key={c.label} pad="20px 22px">
            <p style={{ fontSize: 13, fontWeight: 700, color: color.ink, margin: '0 0 20px' }}>
              {c.label}
            </p>
            <p
              style={{
                fontFamily: font.display,
                fontWeight: 600,
                fontSize: 30,
                lineHeight: 1,
                color: color.navy,
                margin: 0,
              }}
            >
              {c.value}
            </p>
            <p style={{ fontSize: 12, fontWeight: 700, color: toneColor(c.tone), margin: '12px 0 0' }}>
              {c.delta}
            </p>
          </Card>
        ))}
      </div>

      <Card pad="22px 24px">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
          <CardTitle style={{ flex: 1 }}>Daily visits · last 30 days</CardTitle>
          {a.cards[0] && (
            <span style={{ fontSize: 13, fontWeight: 700, color: toneColor(a.cards[0].tone) }}>
              {a.cards[0].delta}
            </span>
          )}
        </div>
        <div style={{ height: DAY_PLOT, display: 'flex', alignItems: 'flex-end', gap: 5 }}>
          {a.visits.map((v, i) => (
            <div
              key={i}
              title={`Day ${i + 1} — ${num(v)} visits`}
              className="hov-bar-blue"
              style={{
                flex: 1,
                height: Math.max(3, Math.round((v / dayMax) * DAY_PLOT)),
                background: color.tint2,
                borderRadius: '4px 4px 2px 2px',
                minWidth: 0,
              }}
            />
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 11,
            color: color.muted,
            marginTop: 8,
          }}
        >
          <span>30 days ago</span>
          <span>15 days ago</span>
          <span>Today</span>
        </div>
      </Card>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Card pad="22px 24px">
          <CardTitle style={{ marginBottom: 12 }}>Top pages</CardTitle>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr',
              gap: 10,
              padding: '8px 2px',
              borderBottom: `1px solid ${color.line}`,
            }}
          >
            <ColHead>Page</ColHead>
            <ColHead align="right">Views</ColHead>
            <ColHead align="right">Trend</ColHead>
          </div>
          {a.pages.map((p) => (
            <div
              key={p.path}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr',
                gap: 10,
                padding: '11px 2px',
                borderBottom: `1px solid ${color.line}`,
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: color.ink,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={p.path}
              >
                {p.path}
              </span>
              <span
                style={{
                  fontSize: 13.5,
                  color: color.ink,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {num(p.views)}
              </span>
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  color: toneColor(p.tone),
                  textAlign: 'right',
                }}
              >
                {p.delta || '—'}
              </span>
            </div>
          ))}
        </Card>

        <Card pad="22px 24px">
          <CardTitle style={{ marginBottom: 16 }}>Traffic sources</CardTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {a.sources.map((s) => (
              <div key={s.label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: color.ink }}>{s.label}</span>
                  <span
                    style={{ fontSize: 13, color: color.muted, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {s.share}%
                  </span>
                </div>
                <Meter pct={s.share} height={8} />
              </div>
            ))}
          </div>
        </Card>
      </div>

      {donutSlices.length > 0 && (
        <Card pad="22px 24px">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <CardTitle>Submissions by form</CardTitle>
            <span style={{ fontSize: 12, color: color.muted }}>All time</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 22,
              marginTop: 14,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ position: 'relative', width: 150, height: 150, flex: 'none' }}>
              <Donut slices={donutSlices} total={donutTotal} />
              <span
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    fontFamily: font.display,
                    fontWeight: 600,
                    fontSize: 24,
                    color: color.navy,
                    lineHeight: 1,
                  }}
                >
                  {num(donutTotal)}
                </span>
                <span style={{ fontSize: 11, color: color.muted }}>total</span>
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 9,
                flex: 1,
                minWidth: 150,
                maxWidth: 380,
              }}
            >
              {donutSlices.map((s) => (
                <span
                  key={s.label}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: color.ink }}
                >
                  <span
                    style={{ width: 9, height: 9, borderRadius: '50%', background: s.color, flex: 'none' }}
                  />
                  <span style={{ flex: 1 }}>{s.label}</span>
                  <span style={{ color: color.muted, fontVariantNumeric: 'tabular-nums' }}>
                    {donutTotal > 0 ? Math.round((s.value / donutTotal) * 100) : 0}%
                  </span>
                </span>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
